import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useDialog } from '@/shared/composables/useDialog';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { myLettagaEntitlementApi } from '../api/inviteApi';

/**
 * My Lettaga 기능 페이지(Members·Chat) 접근 가드.
 * - 구독(usesMyLettaga) 미구독이면 안내 후 구독 화면으로 이동
 * - 기능(features: chat/documents) OFF면 안내 후 설정으로 유도
 * entitlement는 firmCd(세션) 기준. 조회 실패 시엔 UX 가드일 뿐이라 접근 허용(안전측 폴백).
 */
export function useMyLettagaAccess() {
  const router = useRouter();
  const { confirm } = useDialog();
  const languageStore = useLanguageStore();
  const { createSyncT } = useTranslation();
  const t = createSyncT(computed(() => languageStore.currentLanguage.value));
  const subscribed = ref(true);
  const features = ref<string[]>([]);
  const loaded = ref(false);

  function withFeature(template: string, feature: string): string {
    return template.split('{feature}').join(feature);
  }

  async function fetchState() {
    try {
      const r: any = await myLettagaEntitlementApi.get();
      const ent = r?.data?.entitlement;
      subscribed.value = ent ? !!ent.usesMyLettaga : true;
      features.value = (ent?.features || []).map(String);
    } catch { subscribed.value = true; features.value = []; }
    loaded.value = true;
  }

  const hasFeature = (key: string) => features.value.includes(key);

  /** 페이지 진입 가드. 접근 허용 시 true, 막히면 안내·이동 후 false. */
  async function ensureAccess(feature?: string): Promise<boolean> {
    await fetchState();
    if (!subscribed.value) {
      const go = await confirm({
        title: t('myLettaga.access.subscriptionRequiredTitle'),
        message: t('myLettaga.access.subscriptionRequiredMessage'),
        type: 'warning',
        confirmText: t('myLettaga.access.goToSubscription'),
        cancelText: t('myLettaga.common.cancel'),
      });
      router.push(go ? '/mylettaga/subscription?from=settings' : '/dashboard');
      return false;
    }
    if (feature && !hasFeature(feature)) {
      const name = featureName(feature);
      const go = await confirm({
        title: withFeature(t('myLettaga.access.featureUnavailableTitle'), name),
        message: withFeature(t('myLettaga.access.featureUnavailableMessage'), name),
        type: 'warning',
        confirmText: t('myLettaga.access.goToSettings'),
        cancelText: t('myLettaga.common.cancel'),
      });
      router.push(go ? '/mylettaga/settings' : '/dashboard');
      return false;
    }
    return true;
  }

  /** 특정 기능 OFF 안내(페이지 이동 없이 버튼 클릭 등에서 사용). */
  async function notifyFeatureOff(feature: string) {
    const name = featureName(feature);
    const go = await confirm({
      title: withFeature(t('myLettaga.access.featureUnavailableTitle'), name),
      message: withFeature(t('myLettaga.access.featureOffMessage'), name),
      type: 'warning',
      confirmText: t('myLettaga.access.goToSettings'),
      cancelText: t('myLettaga.common.cancel'),
    });
    if (go) router.push('/mylettaga/settings');
  }

  function featureName(key: string) {
    if (key === 'chat') return t('myLettaga.settings.feature_chat_label');
    if (key === 'documents') return t('myLettaga.settings.feature_documents_label');
    return key;
  }

  return { subscribed, features, loaded, hasFeature, ensureAccess, notifyFeatureOff };
}

/**
 * 라우트 가드용 My Lettaga 구독 확인 — beforeEnter에서 호출.
 * useRouter를 쓰지 않아(컴포넌트/라우터 인스턴스 불필요) 네비게이션 가드 안에서 안전하게 동작.
 * 미구독이면 안내 모달을 띄우고, 진입을 막는다(구독 화면으로 유도 또는 현재 위치 유지).
 * 조회 실패 시엔 UX 가드일 뿐이라 접근 허용(안전측 폴백) — 기존 ensureAccess와 동일 정책.
 * @returns allowed=true면 진입 허용. false면 redirectTo(있으면 그 경로로) / null(취소=이동 없음).
 */
export async function guardMyLettagaSubscription(): Promise<{ allowed: boolean; redirectTo: string | null }> {
  let subscribed = true;
  try {
    const r: any = await myLettagaEntitlementApi.get();
    const ent = r?.data?.entitlement;
    subscribed = ent ? !!ent.usesMyLettaga : true;
  } catch { subscribed = true; }
  if (subscribed) return { allowed: true, redirectTo: null };

  const { confirm } = useDialog();
  const languageStore = useLanguageStore();
  const { createSyncT } = useTranslation();
  const t = createSyncT(computed(() => languageStore.currentLanguage.value));
  const go = await confirm({
    title: t('myLettaga.access.subscriptionRequiredTitle'),
    message: t('myLettaga.access.subscriptionRequiredMessage'),
    type: 'warning',
    confirmText: t('myLettaga.access.goToSubscription'),
    cancelText: t('myLettaga.common.cancel'),
  });
  return { allowed: false, redirectTo: go ? '/mylettaga/subscription?from=settings' : null };
}
